"use client"

import * as React from 'react'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@open-mercato/ui/primitives/dialog'
import { Button } from '@open-mercato/ui/primitives/button'
import { Input } from '@open-mercato/ui/primitives/input'
import { DateTimePicker } from '@open-mercato/ui/backend/inputs/DateTimePicker'
import { Label } from '@open-mercato/ui/primitives/label'
import { DISPATCH_STATUSES, type DispatchStatus } from './lookups'
import type { DispatchListItem } from './dispatchTable'

type DispatchEditDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  dispatch: DispatchListItem | null
  onSuccess: () => void
}

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  return iso.slice(0, 16)
}

export function DispatchEditDialog({ open, onOpenChange, dispatch, onSuccess }: DispatchEditDialogProps) {
  const t = useT()
  const { runMutation } = useGuardedMutation({
    contextId: dispatch ? `dispatch-edit-${dispatch.id}` : 'dispatch-edit',
  })

  const [status, setStatus] = React.useState<DispatchStatus | ''>('')
  const [plannedPickupAt, setPlannedPickupAt] = React.useState<Date | null>(null)
  const [plannedDeliveryAt, setPlannedDeliveryAt] = React.useState<Date | null>(null)
  const [dispatcherNotes, setDispatcherNotes] = React.useState('')
  const [pending, setPending] = React.useState(false)

  React.useEffect(() => {
    if (open && dispatch) {
      setStatus(dispatch.status)
      setPlannedPickupAt(dispatch.plannedPickupAt ? new Date(dispatch.plannedPickupAt) : null)
      setPlannedDeliveryAt(dispatch.plannedDeliveryAt ? new Date(dispatch.plannedDeliveryAt) : null)
      setDispatcherNotes(dispatch.dispatcherNotes ?? '')
    } else if (!open) {
      setStatus('')
      setPlannedPickupAt(null)
      setPlannedDeliveryAt(null)
      setDispatcherNotes('')
    }
  }, [open, dispatch])

  const handleSubmit = React.useCallback(async () => {
    if (!dispatch) return
    setPending(true)
    try {
      const payload: Record<string, unknown> = {
        version: dispatch.version,
      }
      if (status) payload.status = status
      if (plannedPickupAt) payload.plannedPickupAt = plannedPickupAt.toISOString()
      if (plannedDeliveryAt) payload.plannedDeliveryAt = plannedDeliveryAt.toISOString()
      payload.dispatcherNotes = dispatcherNotes || null

      await runMutation({
        context: { kind: 'road.dispatch', record: { id: dispatch.id } },
        mutationPayload: payload,
        operation: async () => {
          const res = await apiCall(`/api/road/dispatch/assignments/${dispatch.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
          if (!res.ok) {
            throw new Error(t('dispatch.assign.messages.updateError', 'Failed to update dispatch.'))
          }
        },
      })
      flash(t('dispatch.assign.messages.updateSuccess', 'Dispatch updated.'), 'success')
      onOpenChange(false)
      onSuccess()
    } catch {
      flash(t('dispatch.assign.messages.updateError', 'Failed to update dispatch.'), 'error')
    } finally {
      setPending(false)
    }
  }, [dispatch, status, plannedPickupAt, plannedDeliveryAt, dispatcherNotes, runMutation, t, onOpenChange, onSuccess])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void handleSubmit()
    if (e.key === 'Escape') onOpenChange(false)
  }, [handleSubmit, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onKeyDown={handleKeyDown} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('dispatch.dispatch.form.title.edit', 'Edit dispatch')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="dispatchStatus">{t('dispatch.table.columns.status', 'Status')}</Label>
            <select
              id="dispatchStatus"
              className="h-9 w-full rounded border px-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as DispatchStatus)}
              disabled={pending}
            >
              {DISPATCH_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`dispatch.lookups.dispatchStatus.${s}`, s)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="editPlannedPickupAt">{t('dispatch.assign.fields.plannedPickupAt', 'Planned pickup')}</Label>
              <DateTimePicker
                value={plannedPickupAt}
                onChange={setPlannedPickupAt}
                disabled={pending}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="editPlannedDeliveryAt">{t('dispatch.assign.fields.plannedDeliveryAt', 'Planned delivery')}</Label>
              <DateTimePicker
                value={plannedDeliveryAt}
                onChange={setPlannedDeliveryAt}
                disabled={pending}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="editDispatcherNotes">{t('dispatch.assign.fields.dispatcherNotes', 'Dispatcher notes')}</Label>
            <Input
              id="editDispatcherNotes"
              value={dispatcherNotes}
              onChange={(e) => setDispatcherNotes(e.target.value)}
              disabled={pending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('dispatch.actions.cancelEdit', 'Cancel')}
          </Button>
          <Button type="button" onClick={() => { void handleSubmit() }} disabled={pending || !dispatch}>
            {pending ? t('dispatch.actions.saving', 'Saving…') : t('dispatch.actions.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
